import type { CombinedError } from 'urql';

/** The values arqam sends with an error (`extensions`), e.g. `previousReading`. */
export type ErrorValues = Record<string, unknown>;

/** Arabic text per error code, fixed or built from the error's values. */
export type ErrorMessages = Record<
  string,
  string | ((values: ErrorValues) => string)
>;

/**
 * An API error in Arabic the user can act on.
 *
 * arqam (docs/changes/33) sends `extensions.code` for every error meant for
 * the user, with its values (e.g. READING_BELOW_PREVIOUS + previousReading),
 * and the constraint name for constraint violations. `messages` maps the
 * codes a screen can trigger; a few codes are handled everywhere. Any other
 * error is masked by arqam: the `fallback` is shown with its id, so it can be
 * found in the server logs.
 */
export function describeError(
  error: CombinedError | undefined,
  fallback: string,
  messages: ErrorMessages = {},
): string {
  if (!error) return fallback;
  if (error.networkError) {
    return 'لا يوجد اتصال بالإنترنت. أعد المحاولة بعد قليل.';
  }
  const extensions = (error.graphQLErrors[0]?.extensions ?? {}) as ErrorValues;
  const code = typeof extensions.code === 'string' ? extensions.code : null;

  // A screen's message for a code, or for a constraint ("CHECK_VIOLATION:x").
  const key =
    code && typeof extensions.constraint === 'string'
      ? `${code}:${extensions.constraint}`
      : null;
  const text = (key && messages[key]) || (code && messages[code]);
  if (text) return typeof text === 'function' ? text(extensions) : text;

  switch (code) {
    case 'UNAUTHENTICATED':
      return 'انتهت الجلسة. أعد تحميل الصفحة وسجّل الدخول.';
    case 'FORBIDDEN':
      return 'لا تملك صلاحية القيام بهذا.';
  }
  if (typeof extensions.errorId === 'string') {
    return `${fallback} (رمز الخطأ: ${extensions.errorId})`;
  }
  return code ? `${fallback} (${code})` : fallback;
}
